import { showToast } from "../common";
import { postJson } from "../api";

const selectionStateCache = new WeakMap();

function getContainer(root) {
  if (root instanceof Element && root.matches(".js-school-class-spn-assignments")) {
    return root;
  }

  if (root instanceof Document || root instanceof Element) {
    return root.querySelector(".js-school-class-spn-assignments");
  }

  return null;
}

function readSelectionState(container) {
  const studentSelections = new Map();

  container.querySelectorAll(".js-school-class-spn-radio:checked").forEach((input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const studentId = Number.parseInt(input.dataset.studentId || "0", 10) || 0;
    const spnId = Number.parseInt(input.value || "0", 10) || 0;
    if (studentId > 0 && spnId > 0) {
      studentSelections.set(studentId, spnId);
    }
  });

  return { studentSelections };
}

function cloneSelectionState(state) {
  return {
    studentSelections: new Map(state?.studentSelections ?? []),
  };
}

function getUniformSpnId(state) {
  const values = Array.from(state.studentSelections.values());
  if (values.length === 0) {
    return 0;
  }

  const firstValue = values[0] || 0;
  return values.every((value) => value === firstValue) ? firstValue : 0;
}

function applySelectionState(container, state) {
  container.querySelectorAll(".js-school-class-spn-radio").forEach((input) => {
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const studentId = Number.parseInt(input.dataset.studentId || "0", 10) || 0;
    const spnId = Number.parseInt(input.value || "0", 10) || 0;
    input.checked = studentId > 0 && state.studentSelections.get(studentId) === spnId;
  });

  const uniformSpnId = getUniformSpnId(state);
  container.querySelectorAll(".js-school-class-spn-assign-all").forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    const spnId = Number.parseInt(button.dataset.spnId || "0", 10) || 0;
    const isActive = uniformSpnId > 0 && spnId === uniformSpnId;
    button.classList.toggle("active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
}

function setInputsDisabled(container, disabled) {
  container.querySelectorAll(".js-school-class-spn-radio, .js-school-class-spn-assign-all").forEach((element) => {
    if (element instanceof HTMLInputElement || element instanceof HTMLButtonElement) {
      element.disabled = disabled;
    }
  });
}

function getBulkButtonSpnLabel(button) {
  const label = String(button.dataset.spnLabel || "").trim();
  return label !== "" ? `SPN \"${label}\"` : "wybranego SPN";
}

function confirmBulkAssignment(container, button, previousState) {
  const studentCount = previousState.studentSelections.size;
  const spnLabel = getBulkButtonSpnLabel(button);
  const message =
    studentCount > 0
      ? `Czy na pewno przypisać wszystkich ${studentCount} uczniów oddziału do ${spnLabel}? Ta operacja nadpisze ich obecne przypisania SPN.`
      : `Czy na pewno przypisać wszystkich uczniów oddziału do ${spnLabel}? Ta operacja nadpisze ich obecne przypisania SPN.`;

  if (window.confirm(message)) {
    return true;
  }

  applySelectionState(container, previousState);
  return false;
}

async function sendAssignmentUpdate(container, payload) {
  const apiUrl = container.dataset.schoolClassSpnAssignmentsApi;
  const csrfToken = container.dataset.schoolClassSpnAssignmentsCsrf || "";
  const schoolClassId = Number.parseInt(container.dataset.schoolClassSpnId || "0", 10) || 0;

  return postJson(
    apiUrl,
    {
      school_class_id: schoolClassId,
      ...payload,
    },
    csrfToken,
  );
}

async function handleRadioChange(container, radio) {
  const previousState = cloneSelectionState(selectionStateCache.get(container) || readSelectionState(container));
  const nextState = cloneSelectionState(previousState);
  const studentId = Number.parseInt(radio.dataset.studentId || "0", 10) || 0;
  const spnId = Number.parseInt(radio.value || "0", 10) || 0;

  nextState.studentSelections.set(studentId, spnId);
  setInputsDisabled(container, true);

  try {
    const payload = await sendAssignmentUpdate(container, {
      student_id: studentId,
      spn_id: spnId,
    });

    applySelectionState(container, nextState);
    selectionStateCache.set(container, nextState);
    if (payload?.toast?.message) {
      showToast(payload.toast.message, { type: payload.toast.type || "success" });
    }
  } catch (error) {
    applySelectionState(container, previousState);
    showToast(String(error?.message || "Nie udało się zapisać przypisania SPN."), { type: "danger" });
  } finally {
    setInputsDisabled(container, false);
  }
}

async function handleBulkRadioChange(container, radio) {
  const previousState = cloneSelectionState(selectionStateCache.get(container) || readSelectionState(container));
  const nextState = cloneSelectionState(previousState);
  const spnId = Number.parseInt(radio.dataset.spnId || "0", 10) || 0;

  if (!confirmBulkAssignment(container, radio, previousState)) {
    return;
  }

  nextState.studentSelections.forEach((_, studentId) => {
    nextState.studentSelections.set(studentId, spnId);
  });

  setInputsDisabled(container, true);

  try {
    const payload = await sendAssignmentUpdate(container, {
      assign_all: true,
      spn_id: spnId,
    });

    applySelectionState(container, nextState);
    selectionStateCache.set(container, nextState);
    if (payload?.toast?.message) {
      showToast(payload.toast.message, { type: payload.toast.type || "success" });
    }
  } catch (error) {
    applySelectionState(container, previousState);
    showToast(String(error?.message || "Nie udało się zbiorczo przypisać SPN."), { type: "danger" });
  } finally {
    setInputsDisabled(container, false);
  }
}

export function initSchoolClassSpnAssignments(root = document) {
  const container = getContainer(root);
  if (!container || container.dataset.schoolClassSpnAssignmentsInitialized === "1") {
    return;
  }

  container.dataset.schoolClassSpnAssignmentsInitialized = "1";
  selectionStateCache.set(container, readSelectionState(container));
  container.addEventListener("change", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.matches(".js-school-class-spn-radio")) {
      await handleRadioChange(container, target);
      return;
    }
  });

  container.addEventListener("click", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const bulkButton = target.closest(".js-school-class-spn-assign-all");
    if (bulkButton instanceof HTMLButtonElement) {
      await handleBulkRadioChange(container, bulkButton);
    }
  });
}
